import { Action, Dispatch, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';

/**
 * An action source is a type of abridged Redux middleware that doesn't handle any actions, only creates new ones.
 *
 * This is a niche use-case predominantly for background or push-based services such as responding to server-sent events or a timer.
 */
export type ActionSource<S = unknown> = (
  api: MiddlewareAPI<Dispatch<Action>, S>
) => void;

type CombineActionSourceState<TSources extends ActionSource[]> =
  TSources[number] extends ActionSource<infer S> ? S : never;

export type CombinedActionSourceMiddleware<TSources extends ActionSource[]> =
  Middleware<unknown, CombineActionSourceState<TSources>, Dispatch<Action>>;

export function combineActionSources<TSources extends ActionSource[]>(options: {
  sources: TSources;
}) {
  const { sources } = options;
  const middleware: CombinedActionSourceMiddleware<TSources> = (store) => {
    // TODO(nibeauli): double check this is safe - there was that issue with 'dispatch' not being valid until after setup and needing to defer
    // this until the (next) handler is called.
    for (const actionSource of sources) {
      actionSource(store);
    }
    // create the base handler
    return (next) => (action) => next(action);
  };
  return middleware;
}
